import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Loading from '../components/Loading';

const MAROON = '#680008';
const CREAM = '#fff8ef';

function Icon({ type, size = 18 }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (type === 'user') return <svg {...common}><path d="M20 21a8 8 0 0 0-16 0"/><circle cx="12" cy="7" r="4"/></svg>;
  if (type === 'text') return <svg {...common}><path d="M5 5h14M12 5v14M8 19h8"/></svg>;
  if (type === 'email') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>;
  if (type === 'phone') return <svg {...common}><rect x="7" y="2.5" width="10" height="19" rx="2"/><path d="M10 5h4M11 18.5h2"/></svg>;
  if (type === 'pencil') return <svg {...common}><path d="m4 16 10-10 4 4L8 20l-4 1 1-5Z"/><path d="m13 7 4 4"/></svg>;
  if (type === 'back') return <svg {...common}><path d="m15 18-6-6 6-6"/></svg>;
  return null;
}

function isValidNigeriaPhone(value) {
  return /^0[7-9][0-9]{9}$/.test(value) || /^[+]234[7-9][0-9]{9}$/.test(value);
}

export default function EditProfile() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', displayName: '', fullName: '', phone: '', email: '' });
  const [originalEmail, setOriginalEmail] = useState('');
  const [avatar, setAvatar] = useState('');
  const [usernameChangedAt, setUsernameChangedAt] = useState(null);
  const [achievementBadgeCount, setAchievementBadgeCount] = useState(0);
  const [featuredAchievementBadges, setFeaturedAchievementBadges] = useState([]);
  const [emailChallenge, setEmailChallenge] = useState(null);
  const [emailCode, setEmailCode] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [allowSelfServiceEmailChange, setAllowSelfServiceEmailChange] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.profile.getMe()
      .then(res => {
        const p = res.data?.data?.profile;
        if (p) {
          const email = p.email || '';
          setForm({
            username: p.username || '',
            displayName: p.displayName || '',
            fullName: p.fullName || '',
            phone: p.phone || '',
            email,
          });
          setOriginalEmail(email);
          setAvatar(p.avatarUrl || '/avatars/avatar_male_01.png?v=20260927');
          setUsernameChangedAt(p.usernameChangedAt || null);
          setAchievementBadgeCount(Number(p.achievementBadgeCount || 0));
          setFeaturedAchievementBadges(Array.isArray(p.featuredAchievementBadges) ? p.featuredAchievementBadges : []);
          setAllowSelfServiceEmailChange(p.allowSelfServiceEmailChange === true);
        }
      })
      .catch(e => setError(e.response?.data?.message || 'Unable to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const applyProfile = (p) => {
    if (!p) return;
    const email = p.email || originalEmail;
    setForm(current => ({
      ...current,
      username: p.username || '',
      displayName: p.displayName || '',
      fullName: p.fullName || '',
      phone: p.phone || '',
      email,
    }));
    setOriginalEmail(email);
    setAvatar(p.avatarUrl || avatar);
    setUsernameChangedAt(p.usernameChangedAt || null);
    if (p.achievementBadgeCount != null) setAchievementBadgeCount(Number(p.achievementBadgeCount || 0));
    if (Array.isArray(p.featuredAchievementBadges)) setFeaturedAchievementBadges(p.featuredAchievementBadges);
    if (p.allowSelfServiceEmailChange !== undefined) setAllowSelfServiceEmailChange(p.allowSelfServiceEmailChange === true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    setEmailError('');
    setEmailMessage('');

    try {
      const nextEmail = form.email.trim().toLowerCase();
      const emailChanged = nextEmail !== originalEmail.toLowerCase();
      if (form.phone && !isValidNigeriaPhone(form.phone.trim())) {
        throw Object.assign(new Error('Phone number must be an 11-digit Nigerian number starting with 07, 08, or 09, or use the +234 international format.'), { profileField: true });
      }

      const res = await api.profile.update({
        username: form.username,
        displayName: form.displayName,
        phone: form.phone,
      });
      applyProfile(res.data?.data?.profile);
      setMessage('Profile updated successfully.');

      if (allowSelfServiceEmailChange && nextEmail && emailChanged) {
        try {
          const emailRes = await api.profile.requestEmailChange(nextEmail);
          const data = emailRes.data?.data;
          setEmailChallenge(data?.challengeId ? { challengeId: data.challengeId, expiresAt: data.expiresAt, email: nextEmail } : null);
          setForm(current => ({ ...current, email: nextEmail }));
          setEmailCode('');
          setEmailMessage('Check your new email for a 6-digit verification code.');
          setEmailError('');
        } catch (emailErr) {
          setEmailError(emailErr.response?.data?.message || 'Could not start the email change.');
        }
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not update profile.');
    } finally {
      setSaving(false);
    }
  };

  const verifyEmail = async (e) => {
    e.preventDefault();
    if (!emailChallenge) return;
    setEmailVerifying(true);
    setEmailError('');
    setEmailMessage('');
    try {
      const res = await api.profile.verifyEmailChange(emailChallenge.challengeId, emailCode.trim());
      applyProfile(res.data?.data?.profile);
      setEmailChallenge(null);
      setEmailCode('');
      setEmailMessage('Email address verified and updated successfully.');
    } catch (e) {
      setEmailError(e.response?.data?.message || 'Could not verify the new email address.');
    } finally {
      setEmailVerifying(false);
    }
  };

  const canChangeUsername = !usernameChangedAt || (Date.now() - new Date(usernameChangedAt).getTime() >= 14 * 24 * 60 * 60 * 1000);
  const featuredBadge = featuredAchievementBadges[0];
  const showAchievementBadge = achievementBadgeCount > 0;
  const achievementLabel = featuredBadge?.achievementName || featuredBadge?.name || 'Achievements';

  if (loading) return <Loading variant="section" label="" />;

  return (
    <main style={page}>
      <header style={header}>
        <button type="button" onClick={() => navigate('/profile')} style={backButton} aria-label="Back to profile">
          <Icon type="back" size={21} />
        </button>
        <h1 style={title}>Edit Profile</h1>
        <span style={headerSpacer} />
      </header>

      <section style={avatarSection} aria-label="Profile avatar">
        <div style={avatarBackdrop}>
          <img src={avatar || '/avatars/avatar_male_01.png?v=20260927'} alt="Current avatar" style={avatarImage} />
          {showAchievementBadge && (
            <div style={achievementBadge} title={achievementLabel} aria-label={achievementLabel + ', ' + achievementBadgeCount + ' achievements'}>
              {featuredBadge?.badgeAsset || featuredBadge?.iconUrl
                ? <img src={featuredBadge.badgeAsset || featuredBadge.iconUrl} alt="" style={achievementImage} />
                : <span style={achievementTrophy}>♛</span>}
              <span style={achievementCount}>{achievementBadgeCount}</span>
            </div>
          )}
          <button type="button" onClick={() => navigate('/profile/avatar')} style={pencilButton} aria-label="Change profile photo" title="Change profile photo">
            <Icon type="pencil" size={17} />
          </button>
        </div>
      </section>

      <section style={card}>
        {error && <div style={alert} role="alert">{error}</div>}
        {message && <div style={success} role="status">{message}</div>}

        <form onSubmit={save}>
          <Field label="Username" icon="user" helper={!canChangeUsername ? 'Username changes are on a 14-day cooldown.' : ''}>
            <input
              value={form.username}
              disabled={!canChangeUsername}
              onChange={e => setForm({ ...form, username: e.target.value })}
              style={{ ...input, ...(!canChangeUsername ? disabledInput : {}) }}
            />
          </Field>

          <Field label="Display Name" icon="text">
            <input
              maxLength={60}
              value={form.displayName}
              onChange={e => setForm({ ...form, displayName: e.target.value })}
              style={input}
            />
          </Field>

          <Field label="Registered Full Name (locked)" icon="user" helper="This name cannot be changed by users. Contact an administrator if it needs correction.">
            <input maxLength={120} value={form.fullName} readOnly disabled style={{ ...input, opacity: 0.72, cursor: 'not-allowed' }} />
          </Field>

          <Field label="Phone Number" icon="phone" helper="Use 08012345678 or +2348012345678. It is stored in +234 format.">
            <input
              inputMode="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              style={input}
              placeholder="08012345678"
            />
          </Field>

          <Field
            label="Email"
            icon="email"
            helper={allowSelfServiceEmailChange
              ? "Changing email requires a verification code sent to the new address."
              : "Email is tied to your account and can't be changed here. Contact support if you need to update it."}
          >
            <input
              type="email"
              autoComplete="email"
              value={form.email}
              readOnly={!allowSelfServiceEmailChange}
              disabled={!allowSelfServiceEmailChange}
              onChange={e => setForm({ ...form, email: e.target.value })}
              style={{ ...input, ...(!allowSelfServiceEmailChange ? disabledInput : {}) }}
            />
          </Field>

          <div style={divider} />
          <button disabled={saving} type="submit" style={{ ...saveButton, ...(saving ? saveButtonDisabled : {}) }}>
            {saving ? 'Saving...' : 'SAVE PROFILE'}
          </button>
        </form>

        {emailChallenge && (
          <form onSubmit={verifyEmail} style={emailVerifyCard}>
            <div style={emailVerifyTitle}>Verify new email</div>
            <div style={emailVerifyText}>Enter the 6-digit code sent to <strong>{emailChallenge.email}</strong>. Your current email remains your login email until this is verified.</div>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={emailCode}
              onChange={e => setEmailCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              style={input}
              placeholder="000000"
              aria-label="Email verification code"
            />
            <button disabled={emailVerifying || emailCode.length !== 6} type="submit" style={{ ...verifyButton, ...(emailVerifying || emailCode.length !== 6 ? saveButtonDisabled : {}) }}>
              {emailVerifying ? 'VERIFYING...' : 'VERIFY EMAIL'}
            </button>
            {emailError && <div style={alert} role="alert">{emailError}</div>}
            {emailMessage && <div style={success} role="status">{emailMessage}</div>}
          </form>
        )}

        {!emailChallenge && emailMessage && <div style={emailNotice} role="status">{emailMessage}</div>}
        {emailError && !emailChallenge && <div style={alert} role="alert">{emailError}</div>}
      </section>
    </main>
  );
}

function Field({ label, icon, helper, children }) {
  return (
    <div style={field}>
      <div style={fieldHead}>
        <div style={fieldLabel}><Icon type={icon} size={17} /><span>{label}</span></div>
      </div>
      {children}
      {helper && <div style={hint}>{helper}</div>}
    </div>
  );
}

const page = { minHeight: '100vh', maxWidth: 680, margin: '0 auto', padding: '16px 16px 44px', boxSizing: 'border-box', color: '#222' };
const header = { display: 'grid', gridTemplateColumns: '42px 1fr 42px', alignItems: 'center', minHeight: 48, marginBottom: 8 };
const backButton = { width: 40, height: 40, display: 'grid', placeItems: 'center', border: 0, borderRadius: '50%', background: 'transparent', color: '#555', cursor: 'pointer', padding: 0 };
const headerSpacer = { width: 40, height: 40 };
const title = { margin: 0, textAlign: 'center', fontSize: 20, lineHeight: 1.2, fontWeight: 900, color: '#222' };
const avatarSection = { display: 'grid', placeItems: 'center', padding: '8px 0 26px' };
const avatarBackdrop = { width: 190, height: 190, borderRadius: '50%', background: CREAM, position: 'relative', display: 'grid', placeItems: 'center', boxShadow: '0 4px 18px rgba(0,0,0,.05)' };
const avatarImage = { width: 142, height: 142, borderRadius: '50%', objectFit: 'cover', display: 'block', border: '4px solid #fff', boxShadow: '0 5px 18px rgba(0,0,0,.12)' };
const pencilButton = { position: 'absolute', right: 8, bottom: 8, width: 40, height: 40, borderRadius: '50%', border: '3px solid #fff', background: MAROON, color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', padding: 0, boxShadow: '0 4px 12px rgba(0,0,0,.18)' };
const achievementBadge = { position: 'absolute', left: 8, bottom: 8, width: 45, height: 45, borderRadius: '50%', border: '3px solid #fff', background: '#f5f5f5', color: MAROON, display: 'grid', placeItems: 'center', boxSizing: 'border-box', boxShadow: '0 4px 12px rgba(0,0,0,.14)', overflow: 'hidden' };
const achievementImage = { width: 27, height: 27, objectFit: 'contain' };
const achievementTrophy = { fontSize: 17, lineHeight: 1 };
const achievementCount = { position: 'absolute', right: -1, bottom: -1, minWidth: 15, height: 15, padding: '0 3px', borderRadius: 99, background: MAROON, color: '#fff', fontSize: 8, fontWeight: 900, display: 'grid', placeItems: 'center', boxSizing: 'border-box' };
const card = { background: '#fff', borderRadius: 24, padding: '24px 20px 20px', boxShadow: '0 8px 28px rgba(0,0,0,.07)', border: '1px solid #eee' };
const field = { marginBottom: 18 };
const fieldHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8, padding: '0 2px' };
const fieldLabel = { display: 'flex', alignItems: 'center', gap: 7, color: '#444', fontSize: 12, fontWeight: 900, letterSpacing: '.02em' };
const input = { width: '100%', minHeight: 48, boxSizing: 'border-box', border: '1px solid #e5e5e5', borderRadius: 999, background: '#f5f5f5', color: '#222', padding: '13px 16px', fontSize: 14, outline: 'none', fontFamily: 'inherit' };
const disabledInput = { opacity: .62, cursor: 'not-allowed', background: '#eee' };
const hint = { marginTop: 6, paddingLeft: 4, color: '#777', fontSize: 11, lineHeight: 1.4 };
const divider = { borderTop: '1px dashed #ddd', margin: '4px 0 20px' };
const saveButton = { width: '100%', minHeight: 52, border: 0, borderRadius: 999, background: MAROON, color: '#fff', fontSize: 13, letterSpacing: '.06em', fontWeight: 900, cursor: 'pointer', boxShadow: '0 7px 18px rgba(104,0,8,.18)' };
const verifyButton = { ...saveButton, marginTop: 12 };
const saveButtonDisabled = { opacity: .62, cursor: 'not-allowed', boxShadow: 'none' };
const alert = { background: '#fff', border: '1px solid #ddd', color: '#7a1b1b', padding: 11, borderRadius: 12, marginBottom: 14, fontSize: 12, lineHeight: 1.4 };
const success = { background: '#fff', border: '1px solid #ddd', color: '#555', padding: 11, borderRadius: 12, marginBottom: 14, fontSize: 12, lineHeight: 1.4 };
const emailVerifyCard = { marginTop: 20, paddingTop: 20, borderTop: '1px solid #eee' };
const emailVerifyTitle = { fontSize: 14, fontWeight: 900, color: '#333', marginBottom: 7 };
const emailVerifyText = { color: '#666', fontSize: 12, lineHeight: 1.5, marginBottom: 12 };
const emailNotice = { marginTop: 16, background: '#fff', border: '1px solid #ddd', color: '#555', padding: 11, borderRadius: 12, fontSize: 12, lineHeight: 1.4 };
